/* eslint-disable @typescript-eslint/no-unsafe-type-assertion -- assertion needed for narrowing */
/* eslint-disable @typescript-eslint/no-magic-numbers -- numbers */
/* eslint-disable no-magic-numbers -- numbers needed */
/* eslint-disable complexity -- needed to cover cases */
// src/services/polling/PollingClient.ts
import { PollingError } from '@/services/polling/PollingError';
import { toAbsolute } from '@/utils/format';
import {
  dispatchPollingError,
  dispatchPollingSuccess,
  isRetryableStatus,
} from '@/utils/polling';
import { MIN_LEN, POLLING_STATUS } from '@/constants';
import { POLLING_CONFIG } from '@/constants/polling.constants';
import type {
  PollOptions,
  PollResult,
  PollingResult,
  PollingStatusKey,
} from '@/types/polling.types';
import type { WrappedAPIResponse } from '@/types/api.types';

/* ---------------------- Type helpers / guards ---------------------- */

/**
 * Narrowing type for any object that looks like the Wrapped API envelope:
 * { httpCode: number; success: boolean; message: string; payload: P }
 */
function isWrappedApiResponse<T = unknown>(value: unknown): value is WrappedAPIResponse<T> {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.httpCode === 'number' &&
    typeof candidate.success === 'boolean' &&
    typeof candidate.message === 'string' &&
    'payload' in candidate
  );
}

/** Unwrapped body that the poll handlers will consume (after removing payload/data/result) */
type UnwrappedBody = Record<string, unknown> | null | undefined;

/** Check for a `status` string on an object */
function hasStatusField(value: unknown): value is PollingResult {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.status === 'string' && typeof candidate.id === 'string';
}

/** Check for a `resourceUrl` field on an object */
function hasResourceUrlField(value: unknown): value is { resourceUrl: string | null } {
  if (value === null || typeof value !== 'object') return false;
  return 'resourceUrl' in value;
}

/** Extract a string `message` field from an object, if present */
function getMessageField(value: unknown): string | undefined {
  if (value === null || typeof value !== 'object') return undefined;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.message === 'string' ? candidate.message : undefined;
}

/* ---------------------- Utilities ---------------------- */

/** Backoff delay with jitter */
function calculateBackoffDelayMs(
  retryAttemptCount: number,
  baseDelayMs: number,
  maxDelayMs: number
): number {
  const exponentialDelayMs = baseDelayMs * Math.pow(2, retryAttemptCount);
  const cappedDelayMs = Math.min(exponentialDelayMs, maxDelayMs);
  const jitterOffsetMs = cappedDelayMs * (Math.random() * 0.4 - 0.2);
  return Math.max(MIN_LEN, Math.floor(cappedDelayMs + jitterOffsetMs));
}

/**
 * Deterministically unwraps envelope layers:
 * - If we received a WrappedAPIResponse, use its payload
 * The returned value is the final object the strategy expects (or `null`).
 */
function unwrapApiEnvelope(responseBody: unknown): UnwrappedBody {
  if (isWrappedApiResponse(responseBody)) {
    const { payload } = responseBody;
    if (payload === null || payload === undefined) return payload;
    if (typeof payload !== 'object') return undefined;
    return payload as Record<string, unknown>;
  }

  return undefined;
}

/**
 * Extract and normalize status string in a type-safe way.
 * Accepts the wrapped response.
 * Returns 'UNKNOWN' if no status can be determined.
 */
function statusFromApi(responseBody: unknown): string {
  // Unwrap to get the payload
  const unwrapped = unwrapApiEnvelope(responseBody);

  // Check for direct status field
  if (hasStatusField(unwrapped)) {
    return unwrapped.status.toUpperCase();
  }

  // Unable to determine status
  return 'UNKNOWN';
}

/* ---------------------- Polling strategy and handlers ---------------------- */

/**
 * Handler contract: receives an unwrapped body and optional modal hooks,
 * returns PollResult | null (to continue polling) or throws (terminal error).
 */
type StatusHandler = (options: {
  unwrappedBody: UnwrappedBody;
  onSuccessModal?: (title: string, message: string, resourceUrl?: string | null) => void;
  onErrorModal?: (title: string, message: string) => void;
}) => PollResult | Promise<PollResult> | null | never;

/** Terminal error handler: never returns (always throws) */
function handleTerminalError(
  unwrappedBody: UnwrappedBody,
  onErrorModal?: (title: string, message: string) => void,
  statusKey?: PollingStatusKey
): never {
  // Use type guards to safely extract status
  const apiStatusString = hasStatusField(unwrappedBody)
    ? unwrappedBody.status
    : 'UNKNOWN';

  const messageDetails = getMessageField(unwrappedBody);
  const errorMessage =
    messageDetails ?? `The operation failed (${statusKey ?? 'ERROR'}) with status: ${apiStatusString}.`;

  // Handle error modal or dispatch error event
  if (onErrorModal !== undefined) {
    onErrorModal('Operation Failed', errorMessage);
  } else {
    dispatchPollingError('Operation Failed', errorMessage, unwrappedBody ?? undefined);
  }

  throw new PollingError(errorMessage, { payload: unwrappedBody, apiStatus: apiStatusString });
}

/** Known polling statuses that we have handlers for */
type KnownPollingStatus = typeof POLLING_STATUS[keyof typeof POLLING_STATUS];

/** Strategy map - using Partial to indicate not all statuses may have handlers */
const pollingStrategy: Partial<Record<KnownPollingStatus, StatusHandler>> = {
  [POLLING_STATUS.READ_STORE_UPDATED]: ({ unwrappedBody, onSuccessModal }) => {
    const resourceUrlCandidate = hasResourceUrlField(unwrappedBody)
      ? unwrappedBody.resourceUrl
      : null;

    const successMessage = 'The operation completed successfully. You can view the updated data now.';

    if (onSuccessModal !== undefined) {
      onSuccessModal('Operation Completed', successMessage, resourceUrlCandidate);
    } else {
      dispatchPollingSuccess(
        'Operation Completed',
        successMessage,
        resourceUrlCandidate,
        unwrappedBody ?? undefined
      );
    }

    return { status: 'succeeded', resourceUrl: resourceUrlCandidate };
  },

  [POLLING_STATUS.PENDING]: () => null,
  [POLLING_STATUS.FABRIC_UPDATED]: () => null,
};

/** Attach known terminal error statuses to the strategy */
const KNOWN_ERROR_STATUSES: readonly PollingStatusKey[] = [
  POLLING_STATUS.ERROR_FABRIC,
  POLLING_STATUS.ERROR_SERVICE,
  POLLING_STATUS.ERROR_BUS,
] as const;

for (const errorStatusKey of KNOWN_ERROR_STATUSES) {
  pollingStrategy[errorStatusKey] = ({ unwrappedBody, onErrorModal }) =>
    handleTerminalError(unwrappedBody, onErrorModal, errorStatusKey);
}

/* ---------------------- Helper for async delay ---------------------- */

// eslint-disable-next-line @typescript-eslint/promise-function-async -- simple wrapper
function delay(ms: number): Promise<void> {
  // eslint-disable-next-line promise/avoid-new -- needed here
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/* ---------------------- Main pollUntilDone (strongly typed) ---------------------- */

/**
 * Polls a status URL until a terminal condition is reached or timeout occurs.
 *
 * Key improvements for type-safety:
 *  - All type guards consistently applied
 *  - No unsafe type assertions
 *  - Proper error handling with logging
 *  - Correct modal/dispatch fallback pattern
 *  - AbortSignal checked after async operations
 */
export async function pollUntilDone(
  statusUrl: string,
  {
    baseUrl,
    headersProvider,
    maxWaitMs = POLLING_CONFIG.MAX_WAIT_MS,
    baseDelayMs = POLLING_CONFIG.BASE_DELAY_MS,
    maxDelayMs = POLLING_CONFIG.MAX_DELAY_MS,
    signal,
    onSuccessModal,
    onErrorModal,
  }: PollOptions & {
    onSuccessModal?: (title: string, message: string, resourceUrl?: string | null) => void;
    onErrorModal?: (title: string, message: string) => void;
  } = {}
): Promise<PollResult> {
  const pollingStartMs = Date.now();
  let retryAttemptCount = 0;
  const absoluteStatusUrl = toAbsolute(statusUrl, baseUrl);

  const providedHeaders = headersProvider?.() ?? {};
  const requestInit: RequestInit = {
    cache: 'no-store',
    headers: Object.assign({}, providedHeaders, { Accept: 'application/json' }),
    signal,
  };

  while (Date.now() - pollingStartMs < maxWaitMs) {
    // Check abort signal at start of each iteration
    if (signal?.aborted === true) {
      throw new PollingError('Aborted', { payload: { reason: 'AbortSignal' } });
    }

    let httpResponse: Response | undefined = undefined;
    try {
      httpResponse = await fetch(absoluteStatusUrl, requestInit);
    } catch (fetchError) {
      // Check if aborted during fetch
      if (signal?.aborted !== false) {
        throw new PollingError('Aborted', { payload: { reason: 'AbortSignal' } });
      }

      const retryDelayMs = calculateBackoffDelayMs(retryAttemptCount++, baseDelayMs, maxDelayMs);
      await delay(retryDelayMs);
      continue;
    }

    if (!httpResponse.ok) {
      if (isRetryableStatus(httpResponse.status)) {
        const retryDelayMs = calculateBackoffDelayMs(retryAttemptCount++, baseDelayMs, maxDelayMs);
        await delay(retryDelayMs);
        continue;
      }

      const networkErrorMessage = `Polling failed with status ${httpResponse.status}`;

      if (onErrorModal !== undefined) {
        onErrorModal('Network Error', networkErrorMessage);
      } else {
        dispatchPollingError('Network Error', networkErrorMessage);
      }

      throw new PollingError(networkErrorMessage, { statusCode: httpResponse.status });
    }
    let parsedResponseBody: unknown = undefined;
    try {
      parsedResponseBody = await httpResponse.json();
    } catch (parseError) {
      // Check if aborted during JSON parsing
      if (signal?.aborted !== false) {
        throw new PollingError('Aborted', { payload: { reason: 'AbortSignal' } });
      }

      const retryDelayMs = calculateBackoffDelayMs(retryAttemptCount++, baseDelayMs, maxDelayMs);
      await delay(retryDelayMs);
      continue;
    }

    // Normalize / unwrap into the object handlers expect
    const unwrappedBody = unwrapApiEnvelope(parsedResponseBody);

    // Determine normalized status
    const normalizedStatus = statusFromApi(parsedResponseBody);

    // Narrow the string to KnownPollingStatus before indexing the strategy map
    function isKnownPollingStatus(value: string): value is KnownPollingStatus {
      const knownStatuses: readonly string[] = Object.values(POLLING_STATUS);
      return knownStatuses.includes(value);
    }

    const statusHandler = isKnownPollingStatus(normalizedStatus)
      ? pollingStrategy[normalizedStatus]
      : undefined;

    if (statusHandler !== undefined) {
      const handlerResult = await statusHandler({
        unwrappedBody,
        onSuccessModal,
        onErrorModal,
      });
      if (handlerResult !== null) return handlerResult;
    }

    // Not handled / not terminal: wait and retry
    const retryDelayMs = calculateBackoffDelayMs(retryAttemptCount++, baseDelayMs, maxDelayMs);
    await delay(retryDelayMs);
  }

  const timeoutMessage = 'The operation took too long to complete.';

  if (onErrorModal !== undefined) {
    onErrorModal('Operation Timed Out', timeoutMessage);
  } else {
    dispatchPollingError('Operation Timed Out', timeoutMessage);
  }

  throw new PollingError('Polling timed out.');
}
